import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const CATEGORIES = [
  { id: 'all', label: 'All', color: '#6b7280' },
  { id: 'rides', label: 'Rides', color: '#3b82f6' },
  { id: 'deliveries', label: 'Deliveries', color: '#8b5cf6' },
  { id: 'payments', label: 'Payments', color: '#10b981' },
  { id: 'driver_issues', label: 'Driver Issues', color: '#f59e0b' },
  { id: 'account', label: 'Account', color: '#ec4899' },
  { id: 'general', label: 'General', color: '#6b7280' },
];

const STATUS_CONFIG = {
  open: { label: 'Open', bg: '#fef3c7', color: '#d97706', dot: '#f59e0b' },
  in_review: { label: 'In Review', bg: '#dbeafe', color: '#1d4ed8', dot: '#3b82f6' },
  awaiting_response: { label: 'Awaiting', bg: '#ede9fe', color: '#6d28d9', dot: '#8b5cf6' },
  resolved: { label: 'Resolved', bg: '#d1fae5', color: '#065f46', dot: '#10b981' },
  closed: { label: 'Closed', bg: '#f3f4f6', color: '#374151', dot: '#9ca3af' },
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: '#6b7280' },
  medium: { label: 'Medium', color: '#f59e0b' },
  high: { label: 'High', color: '#ef4444' },
  urgent: { label: 'Urgent', color: '#dc2626' },
};

const CATEGORY_COLORS = {
  rides: '#3b82f6',
  deliveries: '#8b5cf6',
  payments: '#10b981',
  driver_issues: '#f59e0b',
  account: '#ec4899',
  general: '#6b7280',
};

const TicketList = ({ tickets, selectedTicket, onSelectTicket, loading }) => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [searchQuery, setSearchQuery] = useState('');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const filtered = tickets
    ?.filter(t => activeCategory === 'all' || t?.category === activeCategory)
    ?.filter(t => {
      if (!searchQuery) return true;
      const q = searchQuery?.toLowerCase();
      return (t?.ticket_number?.toLowerCase()?.includes(q) ||
      t?.subject?.toLowerCase()?.includes(q) || t?.user_name?.toLowerCase()?.includes(q));
    })
    ?.sort((a, b) => {
      let aVal = a?.[sortField] || '';
      let bVal = b?.[sortField] || '';
      if (sortField === 'created_at') {
        aVal = new Date(aVal)?.getTime();
        bVal = new Date(bVal)?.getTime();
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d?.toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const SortIcon = ({ field }) => (
    <Icon
      name={sortField === field ? (sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'}
      size={13}
      className="ml-1 opacity-60"
    />
  );

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="p-3 border-b border-gray-100">
        <div className="relative">
          <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search tickets..."
            value={searchQuery}
            onChange={e => setSearchQuery(e?.target?.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
            style={{ '--tw-ring-color': '#D4AF37' }}
          />
        </div>
      </div>
      {/* Category Filters */}
      <div className="px-3 py-2 border-b border-gray-100 flex gap-1.5 overflow-x-auto scrollbar-hide">
        {CATEGORIES?.map(cat => (
          <button
            key={cat?.id}
            onClick={() => setActiveCategory(cat?.id)}
            className="flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-200"
            style={{
              background: activeCategory === cat?.id ? cat?.color : '#f3f4f6',
              color: activeCategory === cat?.id ? '#fff' : '#374151',
            }}
          >
            {cat?.label}
          </button>
        ))}
      </div>
      {/* Table Header */}
      <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 grid grid-cols-12 gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">
        <button className="col-span-2 flex items-center text-left hover:text-gray-700" onClick={() => handleSort('ticket_number')}>
          ID <SortIcon field="ticket_number" />
        </button>
        <button className="col-span-3 flex items-center text-left hover:text-gray-700" onClick={() => handleSort('subject')}>
          Subject <SortIcon field="subject" />
        </button>
        <button className="col-span-2 flex items-center text-left hover:text-gray-700" onClick={() => handleSort('user_name')}>
          User <SortIcon field="user_name" />
        </button>
        <button className="col-span-2 flex items-center text-left hover:text-gray-700" onClick={() => handleSort('ticket_status')}>
          Status <SortIcon field="ticket_status" />
        </button>
        <button className="col-span-2 flex items-center text-left hover:text-gray-700" onClick={() => handleSort('created_at')}>
          Date <SortIcon field="created_at" />
        </button>
        <div className="col-span-1" />
      </div>
      {/* Ticket Rows */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4AF37', borderTopColor: 'transparent' }} />
          </div>
        ) : filtered?.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-gray-400">
            <Icon name="Inbox" size={28} className="mb-2 opacity-40" />
            <p className="text-sm">No tickets found</p>
          </div>
        ) : (
          filtered?.map(ticket => {
            const status = STATUS_CONFIG?.[ticket?.ticket_status] || STATUS_CONFIG?.open;
            const priority = PRIORITY_CONFIG?.[ticket?.priority] || PRIORITY_CONFIG?.medium;
            const catColor = CATEGORY_COLORS?.[ticket?.category] || '#6b7280';
            const isSelected = selectedTicket?.id === ticket?.id;

            return (
              <div
                key={ticket?.id}
                onClick={() => onSelectTicket(ticket)}
                className="px-3 py-2.5 grid grid-cols-12 gap-1 items-center cursor-pointer border-b border-gray-50 transition-all duration-150 hover:bg-blue-50"
                style={{ background: isSelected ? '#f0f4ff' : undefined, borderLeft: isSelected ? '3px solid #0f2044' : '3px solid transparent' }}
              >
                <div className="col-span-2">
                  <span className="text-xs font-mono font-semibold" style={{ color: '#0f2044' }}>{ticket?.ticket_number}</span>
                </div>
                <div className="col-span-3">
                  <p className="text-xs font-medium text-gray-800 truncate">{ticket?.subject}</p>
                  <span
                    className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-xs font-medium"
                    style={{ background: catColor + '18', color: catColor }}
                  >
                    {ticket?.category?.replace('_', ' ')}
                  </span>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-700 truncate">{ticket?.user_name}</p>
                  <span className="text-xs font-medium" style={{ color: priority?.color }}>{priority?.label}</span>
                </div>
                <div className="col-span-2">
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium"
                    style={{ background: status?.bg, color: status?.color }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: status?.dot }} />
                    {status?.label}
                  </span>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-500">{formatDate(ticket?.created_at)}</p>
                </div>
                <div className="col-span-1 flex justify-end">
                  <Icon name="ChevronRight" size={14} className="text-gray-400" />
                </div>
              </div>
            );
          })
        )}
      </div>
      {/* Footer count */}
      <div className="px-3 py-2 border-t border-gray-100 bg-gray-50">
        <p className="text-xs text-gray-500">{filtered?.length || 0} ticket{filtered?.length !== 1 ? 's' : ''}</p>
      </div>
    </div>
  );
};

export default TicketList;
